/**
 * Sales Pipeline API Service
 * Provides API methods for managing sales pipeline data including:
 * - Pipeline configurations
 * - Opportunities
 * - Products
 * - Activities
 */

import { apiClient } from '../apiClient';

// ============================================================================
// TYPES
// ============================================================================

// Pipeline stage configuration
export interface PipelineStageConfig {
  id: string;
  name: string;
  position: number;
  probability: number;
  color: string;
  rottingDays?: number;
  isWon?: boolean;
  isLost?: boolean;
}

// Pipeline configuration
export interface Pipeline {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
  stages: PipelineStageConfig[];
  createdAt: string;
  updatedAt: string;
}

// Pricing model types
export type PricingModel = 'flat_rate' | 'per_user' | 'per_member' | 'tiered' | 'volume' | 'usage_based' | 'transaction' | 'outcome_based' | 'percentage_of_billing' | 'percentage_credit';
export type BillingType = 'one_time' | 'recurring' | 'usage' | 'transaction' | 'outcome' | 'percentage';
export type BillingInterval = 'monthly' | 'quarterly' | 'semi_annual' | 'annual';
export type PercentageBasis = 'total_billing' | 'claims_processed' | 'chargebacks' | 'revenue' | 'custom';

// Price tier for tiered pricing
export interface PriceTier {
  id: string;
  minQuantity: number;
  maxQuantity: number | null;
  unitPrice: number;
  flatFee?: number;
}

// Volume threshold for volume pricing
export interface VolumeThreshold {
  id: string;
  minQuantity: number;
  maxQuantity: number | null;
  unitPrice: number;
}

// Line item in an opportunity
export interface OpportunityLineItem {
  id: string;
  productId?: string;
  itemName: string;
  description?: string;
  pricingModel: PricingModel;
  quantity: number;
  unitPrice: number;
  billingType: BillingType;
  billingInterval?: BillingInterval;
  termMonths?: number;
  discountType?: 'percent' | 'fixed';
  discountValue: number;
  estimatedTransactionVolume?: number;
  transactionRate?: number;
  estimatedOutcomes?: number;
  pricePerOutcome?: number;
  estimatedUsage?: number;
  usagePrice?: number;
  includedUsage?: number;
  percentageRate?: number;
  percentageBasis?: PercentageBasis;
  percentageBasisCustomLabel?: string;
  estimatedBasisAmount?: number;
  isCredit?: boolean;
  lineTotal: number;
  discountAmount: number;
  netAmount: number;
  monthlyRecurring: number;
  annualRecurring: number;
}

// Opportunity financials summary
export interface OpportunityFinancials {
  totalOneTime: number;
  totalMrr: number;
  totalArr: number;
  estimatedUsageRevenue: number;
  estimatedTransactionRevenue: number;
  estimatedOutcomeRevenue: number;
  estimatedPercentageRevenue: number;
  totalCredits: number;
  subtotal: number;
  totalDiscount: number;
  grandTotal: number;
  totalContractValue: number;
}

// Prospect relationship types
export type ProspectRelationshipType = 'sponsor_org' | 'cbo' | 'strategic_partner' | 'advisor' | 'investor' | 'other';

// Document types
export type DocumentType = 'nda' | 'msa' | 'sow' | 'proposal' | 'amendment' | 'addendum' | 'other';
export type SignedStatus = 'unsigned' | 'pending_signature' | 'signed' | 'expired' | 'voided';
export type FeeType = 'setup' | 'recurring' | 'milestone' | 'usage' | 'license' | 'support' | 'training' | 'other';
export type FeeStatus = 'pending' | 'invoiced' | 'paid' | 'overdue' | 'waived' | 'cancelled';
export type TranscriptionStatus = 'pending' | 'uploading' | 'transcribing' | 'analyzing' | 'completed' | 'failed';

// Opportunity
export interface Opportunity {
  id: string;
  name: string;
  company: string;
  companyId?: number;
  amount: number;
  stage: string;
  probability: number;
  closeDate: string;
  owner: string;
  lastActivity: string;
  source?: string;
  notes?: string;
  contacts?: string[];
  createdAt?: string;
  pipelineId?: string;
  lineItems?: OpportunityLineItem[];
  financials?: OpportunityFinancials;
  contractTermMonths?: number;
  customerId?: string;
  // Sales opportunity enhancement fields
  referredByContactId?: string;
  referredByName?: string;
  referralSource?: string;
  prospectRelationshipType?: ProspectRelationshipType;
  prelimContext?: string;
  needsOverview?: string;
  demoCompleted?: boolean;
  isDemoRecord?: boolean;
}

// Opportunity Document
export interface OpportunityDocument {
  id: string;
  documentType: DocumentType;
  documentName: string;
  description?: string;
  fileUrl: string;
  fileSizeBytes?: number;
  fileMimeType?: string;
  uploadedBy?: number;
  uploadDate?: string;
  signedStatus: SignedStatus;
  signedDate?: string;
  signedBy?: string;
  effectiveDate?: string;
  expiryDate?: string;
  version: number;
  isCurrent: boolean;
  aiExtractedTerms?: Record<string, any>;
  aiExtractionStatus?: string;
  aiExtractionConfidence?: number;
  notes?: string;
  createdAt?: string;
}

// SOW Fee Schedule Item
export interface FeeScheduleItem {
  id: string;
  documentId?: string;
  feeDescription: string;
  feeType: FeeType;
  amount: number;
  currency: string;
  billingFrequency?: string;
  dueDate?: string;
  startDate?: string;
  endDate?: string;
  paymentTerms?: string;
  status: FeeStatus;
  invoicedDate?: string;
  paidDate?: string;
  paidAmount?: number;
  autoExtracted: boolean;
  extractionConfidence?: number;
  sortOrder: number;
  notes?: string;
  createdAt?: string;
  documentName?: string;
}

// Meeting Transcription
export interface MeetingTranscription {
  id: string;
  activityId?: string;
  opportunityId?: string;
  transcriptText?: string;
  transcriptSegments?: Array<{
    start_time: number;
    end_time: number;
    speaker: string;
    text: string;
    confidence?: number;
  }>;
  aiSummary?: string;
  aiSuggestedFollowUps?: any[];
  aiExtractedNeeds?: any[];
  aiSentimentAnalysis?: any;
  aiKeyDiscussionPoints?: any[];
  aiPreMeetingBrief?: string;
  status: TranscriptionStatus;
  providerName?: string;
  durationSeconds?: number;
  speakerCount?: number;
  createdAt?: string;
  processedAt?: string;
  errorMessage?: string;
}

// Upcoming Sales Meeting
export interface UpcomingSalesMeeting {
  activityId: string;
  title: string;
  description?: string;
  opportunityId?: string;
  dueDate?: string;
  agenda?: string;
  calendarEventId?: string;
  opportunityName?: string;
  company?: string;
  stage?: string;
  startTime?: string;
  endTime?: string;
  meetingLink?: string;
  calendarStatus?: string;
}

// Product catalog item
export interface Product {
  id: string;
  name: string;
  description?: string;
  sku?: string;
  category?: string;
  pricingModel: PricingModel;
  unitPrice: number;
  billingType: BillingType;
  billingInterval?: BillingInterval;
  priceTiers?: PriceTier[];
  volumeThresholds?: VolumeThreshold[];
  transactionRate?: number;
  usageRate?: number;
  minimumFee?: number;
  percentageRate?: number;
  percentageBasis?: PercentageBasis;
  percentageBasisCustomLabel?: string;
  isCredit?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Lead
export interface Lead {
  id: string;
  name: string;
  email: string;
  company: string;
  companyId?: number;
  source: string;
  score: number;
  status: 'new' | 'contacted' | 'qualified' | 'unqualified';
  createdAt: string;
}

// Activity
export interface Activity {
  id: string;
  type: 'call' | 'email' | 'meeting' | 'task' | 'note' | 'stage_change';
  title: string;
  description?: string;
  contact: string;
  opportunityId?: string;
  dueDate: string;
  completedAt?: string;
  completed: boolean;
  priority: 'high' | 'medium' | 'low';
  createdBy?: string;
  createdAt?: string;
  direction?: 'inbound' | 'outbound';
  emailId?: string;
  sharedWithOrg?: boolean;
  metadata?: {
    from_email?: string;
    to_emails?: string[];
    cc_emails?: string[];
    provider?: string;
    folder?: string;
  };
}

// IMAP Integration
export interface IMAPIntegration {
  id: number;
  provider: string;
  connection_type: string;
  email_address: string;
  is_active: boolean;
  sync_enabled: boolean;
  sync_folders: string[];
  last_sync_at?: string;
  last_sync_error?: string;
  consecutive_errors: number;
}

export interface IMAPFolder {
  name: string;
  flags: string[];
  delimiter: string;
}

// ============================================================================
// API RESPONSE TYPES
// ============================================================================

interface ApiListResponse<T> {
  data: T[];
  total: number;
  page: number;
  pageSize: number;
}

// ============================================================================
// LOCAL STORAGE KEYS (for fallback)
// ============================================================================

function getOrgSuffix(): string {
  const cboId = typeof window !== 'undefined' ? localStorage.getItem('active_cbo_id') : null;
  return cboId ? `_${cboId}` : '';
}

/**
 * Safely set a localStorage item, handling QuotaExceededError.
 * If the data is too large, it attempts to store a truncated version (most recent items).
 * If that still fails, it clears the key and logs a warning.
 */
function safeLocalStorageSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    console.warn(`localStorage quota exceeded for key "${key}" (${(value.length / 1024).toFixed(1)} KB). Clearing stale data.`);
    try {
      // Remove the offending key to free space, then try a smaller payload
      localStorage.removeItem(key);
    } catch {
      // If even removeItem fails, nothing more we can do
    }
  }
}

const STORAGE_KEYS = {
  get PIPELINES() { return `sales_pipelines_config${getOrgSuffix()}`; },
  get ACTIVE_PIPELINE() { return `sales_active_pipeline_id${getOrgSuffix()}`; },
  get OPPORTUNITIES() { return `sales_pipeline_opportunities${getOrgSuffix()}`; },
  get PRODUCTS() { return `sales_pipeline_products${getOrgSuffix()}`; },
  get LEADS() { return `sales_pipeline_leads${getOrgSuffix()}`; },
  get ACTIVITIES() { return `sales_pipeline_activities${getOrgSuffix()}`; },
};

// ============================================================================
// PIPELINE API
// ============================================================================

export const pipelineAPI = {
  /**
   * Get all pipelines
   */
  async getPipelines(): Promise<Pipeline[]> {
    try {
      const response = await apiClient.get('/api/platform/sales/pipelines');
      return response.data.pipelines || [];
    } catch (error) {
      // Fallback to localStorage
      console.warn('Failed to fetch pipelines from API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.PIPELINES);
      return stored ? JSON.parse(stored) : [];
    }
  },

  /**
   * Get active pipeline ID
   */
  async getActivePipelineId(): Promise<string | null> {
    try {
      const response = await apiClient.get('/api/platform/sales/pipelines/active');
      return response.data.activePipelineId || null;
    } catch (error) {
      // Fallback to localStorage
      return localStorage.getItem(STORAGE_KEYS.ACTIVE_PIPELINE);
    }
  },

  /**
   * Create a new pipeline
   */
  async createPipeline(pipeline: Omit<Pipeline, 'id' | 'createdAt' | 'updatedAt'>): Promise<Pipeline> {
    try {
      const response = await apiClient.post('/api/platform/sales/pipelines', pipeline);
      return response.data;
    } catch (error) {
      // Fallback: create locally and save to localStorage
      console.warn('Failed to create pipeline via API, using localStorage fallback:', error);
      const newPipeline: Pipeline = {
        ...pipeline,
        id: `pipeline_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const pipelines = await this.getPipelines();
      pipelines.push(newPipeline);
      localStorage.setItem(STORAGE_KEYS.PIPELINES, JSON.stringify(pipelines));
      return newPipeline;
    }
  },

  /**
   * Update a pipeline
   */
  async updatePipeline(id: string, updates: Partial<Pipeline>): Promise<Pipeline> {
    try {
      const response = await apiClient.put(`/api/platform/sales/pipelines/${id}`, updates);
      return response.data;
    } catch (error) {
      // Fallback: update locally and save to localStorage
      console.warn('Failed to update pipeline via API, using localStorage fallback:', error);
      const pipelines = await this.getPipelines();
      const index = pipelines.findIndex(p => p.id === id);
      if (index !== -1) {
        pipelines[index] = { ...pipelines[index], ...updates, updatedAt: new Date().toISOString() };
        localStorage.setItem(STORAGE_KEYS.PIPELINES, JSON.stringify(pipelines));
        return pipelines[index];
      }
      throw new Error('Pipeline not found');
    }
  },

  /**
   * Delete a pipeline
   */
  async deletePipeline(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/platform/sales/pipelines/${id}`);
    } catch (error) {
      // Fallback: delete locally from localStorage
      console.warn('Failed to delete pipeline via API, using localStorage fallback:', error);
      const pipelines = await this.getPipelines();
      const filtered = pipelines.filter(p => p.id !== id);
      localStorage.setItem(STORAGE_KEYS.PIPELINES, JSON.stringify(filtered));
    }
  },

  /**
   * Set active pipeline
   */
  async setActivePipeline(id: string): Promise<void> {
    try {
      await apiClient.post('/api/platform/sales/pipelines/active', { pipelineId: id });
    } catch (error) {
      // Fallback to localStorage
      localStorage.setItem(STORAGE_KEYS.ACTIVE_PIPELINE, id);
    }
  },

  /**
   * Save pipelines to storage (for batch updates)
   */
  savePipelines(pipelines: Pipeline[]): void {
    safeLocalStorageSet(STORAGE_KEYS.PIPELINES, JSON.stringify(pipelines));
  },
};

// ============================================================================
// OPPORTUNITIES API
// ============================================================================

export const opportunitiesAPI = {
  /**
   * Get all opportunities
   */
  async getOpportunities(filters?: {
    pipelineId?: string;
    stage?: string;
    ownerId?: string;
    includeWon?: boolean;
    includeLost?: boolean;
  }): Promise<Opportunity[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.pipelineId) params.append('pipeline_id', filters.pipelineId);
      if (filters?.stage) params.append('stage', filters.stage);
      if (filters?.ownerId) params.append('owner_id', filters.ownerId);
      if (filters?.includeWon !== undefined) params.append('include_won', String(filters.includeWon));
      if (filters?.includeLost !== undefined) params.append('include_lost', String(filters.includeLost));

      const response = await apiClient.get(`/api/platform/sales/opportunities?${params.toString()}`);
      return response.data.opportunities || [];
    } catch (error) {
      // Fallback to localStorage
      console.warn('Failed to fetch opportunities from API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.OPPORTUNITIES);
      let opportunities: Opportunity[] = stored ? JSON.parse(stored) : [];

      // Apply filters locally
      if (filters?.pipelineId) {
        opportunities = opportunities.filter(o => !o.pipelineId || o.pipelineId === filters.pipelineId);
      }
      if (filters?.stage) {
        opportunities = opportunities.filter(o => o.stage === filters.stage);
      }

      return opportunities;
    }
  },

  /**
   * Get a single opportunity by ID
   */
  async getOpportunity(id: string): Promise<Opportunity | null> {
    try {
      const response = await apiClient.get(`/api/platform/sales/opportunities/${id}`);
      return response.data;
    } catch (error) {
      // Fallback to localStorage
      const stored = localStorage.getItem(STORAGE_KEYS.OPPORTUNITIES);
      const opportunities: Opportunity[] = stored ? JSON.parse(stored) : [];
      return opportunities.find(o => o.id === id) || null;
    }
  },

  /**
   * Create a new opportunity
   */
  async createOpportunity(opportunity: Omit<Opportunity, 'id' | 'createdAt' | 'lastActivity'>): Promise<Opportunity> {
    try {
      const response = await apiClient.post('/api/platform/sales/opportunities', opportunity);
      return response.data;
    } catch (error) {
      // Fallback: create locally and save to localStorage
      console.warn('Failed to create opportunity via API, using localStorage fallback:', error);
      const newOpportunity: Opportunity = {
        ...opportunity,
        id: `opp_${Date.now()}`,
        createdAt: new Date().toISOString(),
        lastActivity: new Date().toISOString().split('T')[0],
      };
      const stored = localStorage.getItem(STORAGE_KEYS.OPPORTUNITIES);
      const opportunities: Opportunity[] = stored ? JSON.parse(stored) : [];
      opportunities.unshift(newOpportunity);
      localStorage.setItem(STORAGE_KEYS.OPPORTUNITIES, JSON.stringify(opportunities));
      return newOpportunity;
    }
  },

  /**
   * Update an opportunity
   */
  async updateOpportunity(id: string, updates: Partial<Opportunity>): Promise<Opportunity> {
    try {
      const response = await apiClient.put(`/api/platform/sales/opportunities/${id}`, updates);
      return response.data;
    } catch (error) {
      // Fallback: update locally and save to localStorage
      console.warn('Failed to update opportunity via API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.OPPORTUNITIES);
      const opportunities: Opportunity[] = stored ? JSON.parse(stored) : [];
      const index = opportunities.findIndex(o => o.id === id);
      if (index !== -1) {
        opportunities[index] = {
          ...opportunities[index],
          ...updates,
          lastActivity: new Date().toISOString().split('T')[0]
        };
        localStorage.setItem(STORAGE_KEYS.OPPORTUNITIES, JSON.stringify(opportunities));
        return opportunities[index];
      }
      throw new Error('Opportunity not found');
    }
  },

  /**
   * Update opportunity stage
   */
  async updateStage(id: string, stage: string, probability?: number): Promise<Opportunity> {
    try {
      const response = await apiClient.put(`/api/platform/sales/opportunities/${id}/stage`, {
        stage,
        probability,
      });
      return response.data;
    } catch (error) {
      // Use regular update as fallback
      return this.updateOpportunity(id, { stage, probability });
    }
  },

  /**
   * Delete an opportunity
   */
  async deleteOpportunity(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/platform/sales/opportunities/${id}`);
    } catch (error) {
      // Fallback: delete locally from localStorage
      console.warn('Failed to delete opportunity via API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.OPPORTUNITIES);
      const opportunities: Opportunity[] = stored ? JSON.parse(stored) : [];
      const filtered = opportunities.filter(o => o.id !== id);
      localStorage.setItem(STORAGE_KEYS.OPPORTUNITIES, JSON.stringify(filtered));
    }
  },

  /**
   * Batch save opportunities (for sync)
   */
  saveOpportunities(opportunities: Opportunity[]): void {
    safeLocalStorageSet(STORAGE_KEYS.OPPORTUNITIES, JSON.stringify(opportunities));
  },

  /**
   * Get won opportunities (for RevOps integration)
   */
  async getWonOpportunities(): Promise<Opportunity[]> {
    const stored = localStorage.getItem(STORAGE_KEYS.OPPORTUNITIES);
    const opportunities: Opportunity[] = stored ? JSON.parse(stored) : [];

    // Get pipeline configs to find won stages
    const pipelinesStored = localStorage.getItem(STORAGE_KEYS.PIPELINES);
    const pipelines: Pipeline[] = pipelinesStored ? JSON.parse(pipelinesStored) : [];

    const wonStageIds = new Set(['closed_won']);
    pipelines.forEach(pipeline => {
      pipeline.stages?.forEach(stage => {
        if (stage.isWon) {
          wonStageIds.add(stage.id);
        }
      });
    });

    return opportunities.filter(opp => wonStageIds.has(opp.stage));
  },
};

// ============================================================================
// PRODUCTS API
// ============================================================================

export const productsAPI = {
  /**
   * Get all products
   */
  async getProducts(filters?: {
    category?: string;
    isActive?: boolean;
    pricingModel?: PricingModel;
  }): Promise<Product[]> {
    const getFromLocalStorage = (): Product[] => {
      const stored = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      let products: Product[] = stored ? JSON.parse(stored) : [];
      if (filters?.isActive !== undefined) {
        products = products.filter(p => p.isActive === filters.isActive);
      }
      if (filters?.category) {
        products = products.filter(p => p.category === filters.category);
      }
      return products;
    };

    try {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.isActive !== undefined) params.append('is_active', String(filters.isActive));
      if (filters?.pricingModel) params.append('pricing_model', filters.pricingModel);

      const response = await apiClient.get(`/api/platform/sales/products?${params.toString()}`);
      const apiProducts = response.data.products || [];
      // If API returns empty, fall back to localStorage (backend stub returns empty)
      if (apiProducts.length === 0) {
        return getFromLocalStorage();
      }
      return apiProducts;
    } catch (error) {
      console.warn('Failed to fetch products from API, using localStorage fallback:', error);
      return getFromLocalStorage();
    }
  },

  /**
   * Get a single product by ID
   */
  async getProduct(id: string): Promise<Product | null> {
    try {
      const response = await apiClient.get(`/api/platform/sales/products/${id}`);
      return response.data;
    } catch (error) {
      // Fallback to localStorage
      const stored = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      const products: Product[] = stored ? JSON.parse(stored) : [];
      return products.find(p => p.id === id) || null;
    }
  },

  /**
   * Create a new product
   */
  async createProduct(product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<Product> {
    try {
      const response = await apiClient.post('/api/platform/sales/products', product);
      return response.data;
    } catch (error) {
      // Fallback: create locally and save to localStorage
      console.warn('Failed to create product via API, using localStorage fallback:', error);
      const newProduct: Product = {
        ...product,
        id: `prod_${Date.now()}`,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const stored = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      const products: Product[] = stored ? JSON.parse(stored) : [];
      products.unshift(newProduct);
      localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(products));
      return newProduct;
    }
  },

  /**
   * Update a product
   */
  async updateProduct(id: string, updates: Partial<Product>): Promise<Product> {
    try {
      const response = await apiClient.put(`/api/platform/sales/products/${id}`, updates);
      return response.data;
    } catch (error) {
      // Fallback: update locally and save to localStorage
      console.warn('Failed to update product via API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      const products: Product[] = stored ? JSON.parse(stored) : [];
      const index = products.findIndex(p => p.id === id);
      if (index !== -1) {
        products[index] = {
          ...products[index],
          ...updates,
          updatedAt: new Date().toISOString()
        };
        localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(products));
        return products[index];
      }
      throw new Error('Product not found');
    }
  },

  /**
   * Delete a product
   */
  async deleteProduct(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/platform/sales/products/${id}`);
    } catch (error) {
      // Fallback: delete locally from localStorage
      console.warn('Failed to delete product via API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.PRODUCTS);
      const products: Product[] = stored ? JSON.parse(stored) : [];
      const filtered = products.filter(p => p.id !== id);
      localStorage.setItem(STORAGE_KEYS.PRODUCTS, JSON.stringify(filtered));
    }
  },

  /**
   * Batch save products (for sync)
   */
  saveProducts(products: Product[]): void {
    safeLocalStorageSet(STORAGE_KEYS.PRODUCTS, JSON.stringify(products));
  },
};

// ============================================================================
// ACTIVITIES API
// ============================================================================

export const activitiesAPI = {
  /**
   * Get all activities
   */
  async getActivities(filters?: {
    opportunityId?: string;
    type?: Activity['type'];
    completed?: boolean;
  }): Promise<Activity[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.opportunityId) params.append('opportunity_id', filters.opportunityId);
      if (filters?.type) params.append('type', filters.type);
      if (filters?.completed !== undefined) params.append('completed', String(filters.completed));

      const response = await apiClient.get(`/api/platform/sales/activities?${params.toString()}`);
      return response.data.activities || [];
    } catch (error) {
      // Fallback to localStorage
      console.warn('Failed to fetch activities from API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVITIES);
      let activities: Activity[] = stored ? JSON.parse(stored) : [];

      // Apply filters locally
      if (filters?.opportunityId) {
        activities = activities.filter(a => a.opportunityId === filters.opportunityId);
      }
      if (filters?.type) {
        activities = activities.filter(a => a.type === filters.type);
      }
      if (filters?.completed !== undefined) {
        activities = activities.filter(a => a.completed === filters.completed);
      }

      return activities;
    }
  },

  /**
   * Create a new activity
   */
  async createActivity(activity: Omit<Activity, 'id' | 'createdAt'>): Promise<Activity> {
    try {
      const response = await apiClient.post('/api/platform/sales/activities', activity);
      return response.data;
    } catch (error) {
      // Fallback: create locally and save to localStorage
      console.warn('Failed to create activity via API, using localStorage fallback:', error);
      const newActivity: Activity = {
        ...activity,
        id: `act_${Date.now()}`,
        createdAt: new Date().toISOString(),
      };
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVITIES);
      const activities: Activity[] = stored ? JSON.parse(stored) : [];
      activities.unshift(newActivity);
      localStorage.setItem(STORAGE_KEYS.ACTIVITIES, JSON.stringify(activities));
      return newActivity;
    }
  },

  /**
   * Update an activity
   */
  async updateActivity(id: string, updates: Partial<Activity>): Promise<Activity> {
    try {
      const response = await apiClient.put(`/api/platform/sales/activities/${id}`, updates);
      return response.data;
    } catch (error) {
      // Fallback: update locally and save to localStorage
      console.warn('Failed to update activity via API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVITIES);
      const activities: Activity[] = stored ? JSON.parse(stored) : [];
      const index = activities.findIndex(a => a.id === id);
      if (index !== -1) {
        activities[index] = { ...activities[index], ...updates };
        localStorage.setItem(STORAGE_KEYS.ACTIVITIES, JSON.stringify(activities));
        return activities[index];
      }
      throw new Error('Activity not found');
    }
  },

  /**
   * Complete an activity
   */
  async completeActivity(id: string): Promise<Activity> {
    return this.updateActivity(id, {
      completed: true,
      completedAt: new Date().toISOString(),
    });
  },

  /**
   * Delete an activity
   */
  async deleteActivity(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/platform/sales/activities/${id}`);
    } catch (error) {
      // Fallback: delete locally from localStorage
      console.warn('Failed to delete activity via API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.ACTIVITIES);
      const activities: Activity[] = stored ? JSON.parse(stored) : [];
      const filtered = activities.filter(a => a.id !== id);
      localStorage.setItem(STORAGE_KEYS.ACTIVITIES, JSON.stringify(filtered));
    }
  },

  /**
   * Batch save activities (for sync)
   */
  saveActivities(activities: Activity[]): void {
    safeLocalStorageSet(STORAGE_KEYS.ACTIVITIES, JSON.stringify(activities));
  },

  /**
   * Share an activity with the rest of the organization.
   * Activities are private to their owner by default; this opts in to org-wide visibility.
   */
  async shareActivity(id: string): Promise<void> {
    await apiClient.post(`/api/platform/sales/activities/${id}/share`);
  },

  /**
   * Revoke org-wide sharing on an activity, returning it to private (owner-only).
   */
  async unshareActivity(id: string): Promise<void> {
    await apiClient.post(`/api/platform/sales/activities/${id}/unshare`);
  },
};

// ============================================================================
// LEADS API
// ============================================================================

export const leadsAPI = {
  /**
   * Get all leads
   */
  async getLeads(filters?: {
    status?: Lead['status'];
    source?: string;
  }): Promise<Lead[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.status) params.append('status', filters.status);
      if (filters?.source) params.append('source', filters.source);

      const response = await apiClient.get(`/api/platform/sales/leads?${params.toString()}`);
      return response.data.leads || [];
    } catch (error) {
      // Fallback to localStorage
      console.warn('Failed to fetch leads from API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.LEADS);
      let leads: Lead[] = stored ? JSON.parse(stored) : [];

      // Apply filters locally
      if (filters?.status) {
        leads = leads.filter(l => l.status === filters.status);
      }
      if (filters?.source) {
        leads = leads.filter(l => l.source === filters.source);
      }

      return leads;
    }
  },

  /**
   * Create a new lead
   */
  async createLead(lead: Omit<Lead, 'id' | 'createdAt'>): Promise<Lead> {
    try {
      const response = await apiClient.post('/api/platform/sales/leads', lead);
      return response.data;
    } catch (error) {
      // Fallback: create locally and save to localStorage
      console.warn('Failed to create lead via API, using localStorage fallback:', error);
      const newLead: Lead = {
        ...lead,
        id: `lead_${Date.now()}`,
        createdAt: new Date().toISOString().split('T')[0],
      };
      const stored = localStorage.getItem(STORAGE_KEYS.LEADS);
      const leads: Lead[] = stored ? JSON.parse(stored) : [];
      leads.unshift(newLead);
      localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(leads));
      return newLead;
    }
  },

  /**
   * Update a lead
   */
  async updateLead(id: string, updates: Partial<Lead>): Promise<Lead> {
    try {
      const response = await apiClient.put(`/api/platform/sales/leads/${id}`, updates);
      return response.data;
    } catch (error) {
      // Fallback: update locally and save to localStorage
      console.warn('Failed to update lead via API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.LEADS);
      const leads: Lead[] = stored ? JSON.parse(stored) : [];
      const index = leads.findIndex(l => l.id === id);
      if (index !== -1) {
        leads[index] = { ...leads[index], ...updates };
        localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(leads));
        return leads[index];
      }
      throw new Error('Lead not found');
    }
  },

  /**
   * Delete a lead
   */
  async deleteLead(id: string): Promise<void> {
    try {
      await apiClient.delete(`/api/platform/sales/leads/${id}`);
    } catch (error) {
      // Fallback: delete locally from localStorage
      console.warn('Failed to delete lead via API, using localStorage fallback:', error);
      const stored = localStorage.getItem(STORAGE_KEYS.LEADS);
      const leads: Lead[] = stored ? JSON.parse(stored) : [];
      const filtered = leads.filter(l => l.id !== id);
      localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(filtered));
    }
  },

  /**
   * Batch save leads (for sync)
   */
  saveLeads(leads: Lead[]): void {
    localStorage.setItem(STORAGE_KEYS.LEADS, JSON.stringify(leads));
  },
};

// ============================================================================
// IMAP EMAIL INTEGRATION API
// ============================================================================

export const imapAPI = {
  /**
   * Auto-detect IMAP/SMTP settings from email address
   */
  async detectProvider(email: string) {
    const response = await apiClient.post('/api/crm-activities/imap/detect', { email_address: email });
    return response.data;
  },

  /**
   * Test IMAP connection without saving
   */
  async testConnection(params: {
    email: string;
    password: string;
    imap_host: string;
    imap_port: number;
    imap_use_ssl: boolean;
  }) {
    const { email, ...rest } = params;
    const response = await apiClient.post('/api/crm-activities/imap/test', { email_address: email, ...rest });
    return response.data;
  },

  /**
   * Connect IMAP account (test + encrypt password + save)
   */
  async connect(params: {
    email: string;
    password: string;
    imap_host: string;
    imap_port: number;
    imap_use_ssl: boolean;
    smtp_host: string;
    smtp_port: number;
    smtp_use_tls: boolean;
    provider?: string;
  }): Promise<IMAPIntegration> {
    const { email, ...rest } = params;
    const response = await apiClient.post('/api/crm-activities/imap/connect', { email_address: email, ...rest });
    return response.data;
  },

  /**
   * List available IMAP folders
   */
  async getFolders(integrationId: number): Promise<IMAPFolder[]> {
    const response = await apiClient.get(`/api/crm-activities/imap/${integrationId}/folders`);
    return response.data.folders;
  },

  /**
   * Update selected sync folders
   */
  async updateFolders(integrationId: number, folders: string[]) {
    const response = await apiClient.patch(`/api/crm-activities/imap/${integrationId}/folders`, { folders });
    return response.data;
  },

  /**
   * Trigger manual sync
   */
  async sync(integrationId: number) {
    const response = await apiClient.post(`/api/crm-activities/imap/${integrationId}/sync`);
    return response.data;
  },

  /**
   * Send email via SMTP
   */
  async sendEmail(integrationId: number, params: {
    to: string | string[];
    subject: string;
    body: string;
    cc?: string | string[];
    opportunity_id?: string;
  }) {
    const toList = Array.isArray(params.to) ? params.to : [params.to];
    const ccList = params.cc ? (Array.isArray(params.cc) ? params.cc : [params.cc]) : undefined;
    const response = await apiClient.post(`/api/crm-activities/imap/${integrationId}/send`, {
      to: toList,
      cc: ccList,
      subject: params.subject,
      body_html: params.body,
      opportunity_id: params.opportunity_id,
    });
    return response.data;
  },

  /**
   * Disconnect IMAP integration
   */
  async disconnect(integrationId: number) {
    const response = await apiClient.delete(`/api/crm-activities/imap/${integrationId}`);
    return response.data;
  },
};

// ============================================================================
// EXPORT ALL
// ============================================================================

// ============================================================================
// MEETING INTELLIGENCE API
// ============================================================================

export const meetingIntelligenceAPI = {
  async processRecording(activityId: string, audioUrl: string, opportunityId?: string) {
    const response = await apiClient.post(`/api/platform/sales/activities/${activityId}/process-recording`, {
      audio_url: audioUrl,
      opportunity_id: opportunityId,
    });
    return response.data;
  },

  async uploadAndProcessRecording(activityId: string, file: File | Blob, opportunityId?: string) {
    const formData = new FormData();
    formData.append('file', file, file instanceof File ? file.name : `recording_${Date.now()}.webm`);
    if (opportunityId) formData.append('opportunity_id', opportunityId);

    const response = await apiClient.post(
      `/api/platform/sales/activities/${activityId}/upload-recording`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  async generatePreMeetingBrief(opportunityId: string, upcomingActivityId?: string) {
    const response = await apiClient.post(`/api/platform/sales/opportunities/${opportunityId}/pre-meeting-brief`, {
      upcomingActivityId,
    });
    return response.data;
  },

  async generateFollowUp(activityId: string) {
    const response = await apiClient.post(`/api/platform/sales/activities/${activityId}/generate-follow-up`);
    return response.data;
  },

  async getTranscription(activityId: string): Promise<MeetingTranscription> {
    const response = await apiClient.get(`/api/platform/sales/activities/${activityId}/transcription`);
    return response.data;
  },

  async getUpcomingMeetings(daysAhead: number = 7): Promise<UpcomingSalesMeeting[]> {
    const response = await apiClient.get(`/api/platform/sales/meetings/upcoming?days_ahead=${daysAhead}`);
    return response.data.meetings || [];
  },

  async createMeetingWithCalendar(params: {
    opportunityId?: string;
    title: string;
    description?: string;
    meetingType?: string;
    scheduledStart: string;
    scheduledEnd: string;
    timezone?: string;
    locationType?: string;
    videoLink?: string;
    attendeeUserIds?: number[];
    externalAttendees?: Array<{ email: string; name?: string }>;
    agenda?: string;
    createCalendarEvent?: boolean;
  }) {
    const response = await apiClient.post('/api/platform/sales/meetings/create-with-calendar', {
      opportunity_id: params.opportunityId,
      title: params.title,
      description: params.description,
      meeting_type: params.meetingType || 'meeting',
      scheduled_start: params.scheduledStart,
      scheduled_end: params.scheduledEnd,
      timezone: params.timezone || 'America/Los_Angeles',
      location_type: params.locationType || 'video',
      video_link: params.videoLink,
      attendee_user_ids: params.attendeeUserIds || [],
      external_attendees: params.externalAttendees || [],
      agenda: params.agenda,
      create_calendar_event: params.createCalendarEvent ?? true,
    });
    return response.data;
  },

  async linkActivityToCalendar(activityId: string, calendarEventId: string) {
    const response = await apiClient.post(`/api/platform/sales/activities/${activityId}/link-calendar-event`, {
      calendar_event_id: calendarEventId,
    });
    return response.data;
  },
};

// ============================================================================
// DOCUMENTS API
// ============================================================================

export const documentsAPI = {
  async listDocuments(opportunityId: string, filters?: {
    documentType?: string;
    signedStatus?: string;
  }): Promise<OpportunityDocument[]> {
    const params = new URLSearchParams();
    if (filters?.documentType) params.append('document_type', filters.documentType);
    if (filters?.signedStatus) params.append('signed_status', filters.signedStatus);
    const response = await apiClient.get(`/api/platform/sales/opportunities/${opportunityId}/documents?${params.toString()}`);
    return response.data;
  },

  async uploadDocument(opportunityId: string, doc: {
    documentType: DocumentType;
    documentName: string;
    description?: string;
    fileUrl?: string;
    file?: File;
    cloudSource?: 'onedrive' | 'google_drive';
    cloudFileId?: string;
    fileSizeBytes?: number;
    fileMimeType?: string;
    signedStatus?: SignedStatus;
    effectiveDate?: string;
    expiryDate?: string;
    notes?: string;
    extractTerms?: boolean;
  }) {
    // If a local file is provided, upload via FormData
    if (doc.file) {
      const formData = new FormData();
      formData.append('file', doc.file);
      formData.append('document_type', doc.documentType);
      formData.append('document_name', doc.documentName);
      if (doc.description) formData.append('description', doc.description);
      formData.append('signed_status', doc.signedStatus || 'unsigned');
      if (doc.effectiveDate) formData.append('effective_date', doc.effectiveDate);
      if (doc.expiryDate) formData.append('expiry_date', doc.expiryDate);
      if (doc.notes) formData.append('notes', doc.notes);
      formData.append('extract_terms', String(doc.extractTerms ?? false));

      const response = await apiClient.post(
        `/api/platform/sales/opportunities/${opportunityId}/documents/upload`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return response.data;
    }

    // Cloud file or URL-based upload
    const response = await apiClient.post(`/api/platform/sales/opportunities/${opportunityId}/documents`, {
      document_type: doc.documentType,
      document_name: doc.documentName,
      description: doc.description,
      file_url: doc.fileUrl,
      cloud_source: doc.cloudSource,
      cloud_file_id: doc.cloudFileId,
      file_size_bytes: doc.fileSizeBytes,
      file_mime_type: doc.fileMimeType,
      signed_status: doc.signedStatus || 'unsigned',
      effective_date: doc.effectiveDate,
      expiry_date: doc.expiryDate,
      notes: doc.notes,
      extract_terms: doc.extractTerms ?? false,
    });
    return response.data;
  },

  async updateDocument(opportunityId: string, documentId: string, updates: {
    documentName?: string;
    description?: string;
    signedStatus?: SignedStatus;
    signedDate?: string;
    signedBy?: string;
    counterSignedBy?: string;
    counterSignedDate?: string;
    effectiveDate?: string;
    expiryDate?: string;
    notes?: string;
  }) {
    const response = await apiClient.put(`/api/platform/sales/opportunities/${opportunityId}/documents/${documentId}`, {
      document_name: updates.documentName,
      description: updates.description,
      signed_status: updates.signedStatus,
      signed_date: updates.signedDate,
      signed_by: updates.signedBy,
      counter_signed_by: updates.counterSignedBy,
      counter_signed_date: updates.counterSignedDate,
      effective_date: updates.effectiveDate,
      expiry_date: updates.expiryDate,
      notes: updates.notes,
    });
    return response.data;
  },

  async deleteDocument(opportunityId: string, documentId: string) {
    await apiClient.delete(`/api/platform/sales/opportunities/${opportunityId}/documents/${documentId}`);
  },

  async extractTerms(opportunityId: string, documentId: string) {
    const response = await apiClient.post(`/api/platform/sales/opportunities/${opportunityId}/documents/${documentId}/extract-terms`);
    return response.data;
  },
};

// ============================================================================
// FEE SCHEDULE API
// ============================================================================

export const feeScheduleAPI = {
  async listFees(opportunityId: string, status?: string): Promise<{ items: FeeScheduleItem[]; totalValue: number; count: number }> {
    const params = status ? `?status=${status}` : '';
    const response = await apiClient.get(`/api/platform/sales/opportunities/${opportunityId}/fee-schedule${params}`);
    return response.data;
  },

  async createFee(opportunityId: string, fee: {
    documentId?: string;
    feeDescription: string;
    feeType: FeeType;
    amount: number;
    currency?: string;
    billingFrequency?: string;
    dueDate?: string;
    startDate?: string;
    endDate?: string;
    paymentTerms?: string;
    notes?: string;
    sortOrder?: number;
  }) {
    const response = await apiClient.post(`/api/platform/sales/opportunities/${opportunityId}/fee-schedule`, {
      document_id: fee.documentId,
      fee_description: fee.feeDescription,
      fee_type: fee.feeType,
      amount: fee.amount,
      currency: fee.currency || 'USD',
      billing_frequency: fee.billingFrequency,
      due_date: fee.dueDate,
      start_date: fee.startDate,
      end_date: fee.endDate,
      payment_terms: fee.paymentTerms,
      notes: fee.notes,
      sort_order: fee.sortOrder || 0,
    });
    return response.data;
  },

  async updateFee(opportunityId: string, feeId: string, updates: {
    feeDescription?: string;
    feeType?: FeeType;
    amount?: number;
    billingFrequency?: string;
    dueDate?: string;
    startDate?: string;
    endDate?: string;
    paymentTerms?: string;
    status?: FeeStatus;
    invoicedDate?: string;
    paidDate?: string;
    paidAmount?: number;
    notes?: string;
  }) {
    const response = await apiClient.put(`/api/platform/sales/opportunities/${opportunityId}/fee-schedule/${feeId}`, {
      fee_description: updates.feeDescription,
      fee_type: updates.feeType,
      amount: updates.amount,
      billing_frequency: updates.billingFrequency,
      due_date: updates.dueDate,
      start_date: updates.startDate,
      end_date: updates.endDate,
      payment_terms: updates.paymentTerms,
      status: updates.status,
      invoiced_date: updates.invoicedDate,
      paid_date: updates.paidDate,
      paid_amount: updates.paidAmount,
      notes: updates.notes,
    });
    return response.data;
  },

  async deleteFee(opportunityId: string, feeId: string) {
    await apiClient.delete(`/api/platform/sales/opportunities/${opportunityId}/fee-schedule/${feeId}`);
  },
};

// ============================================================================
// ORG ACTIVATION API
// ============================================================================

export const orgActivationAPI = {
  async activateOrg(opportunityId: string, params: {
    orgId?: number;
    createNewOrg?: boolean;
    orgName?: string;
  }) {
    const response = await apiClient.post(`/api/platform/sales/opportunities/${opportunityId}/activate-org`, {
      org_id: params.orgId,
      create_new_org: params.createNewOrg ?? false,
      org_name: params.orgName,
    });
    return response.data;
  },

  async getSalesHistory(orgId: number) {
    const response = await apiClient.get(`/api/platform/sales/organizations/${orgId}/sales-history`);
    return response.data;
  },

  async getOrgFeeSchedule(orgId: number, status?: string) {
    const params = status ? `?status=${status}` : '';
    const response = await apiClient.get(`/api/platform/sales/organizations/${orgId}/fee-schedule${params}`);
    return response.data;
  },

  async closeWon(opportunityId: string, params: {
    actualCloseDate?: string;
    winReason?: string;
    notes?: string;
  }) {
    const response = await apiClient.post(`/api/platform/sales/opportunities/${opportunityId}/close-won`, {
      actual_close_date: params.actualCloseDate,
      win_reason: params.winReason,
      notes: params.notes,
    });
    return response.data;
  },

  async closeLost(opportunityId: string, params: {
    lossReason?: string;
    competitorName?: string;
    notes?: string;
  }) {
    const response = await apiClient.post(`/api/platform/sales/opportunities/${opportunityId}/close-lost`, {
      loss_reason: params.lossReason,
      competitor_name: params.competitorName,
      notes: params.notes,
    });
    return response.data;
  },
};

// ============================================================================
// EXPORT ALL
// ============================================================================

export const salesPipelineAPI = {
  pipelines: pipelineAPI,
  opportunities: opportunitiesAPI,
  products: productsAPI,
  activities: activitiesAPI,
  leads: leadsAPI,
  meetingIntelligence: meetingIntelligenceAPI,
  documents: documentsAPI,
  feeSchedule: feeScheduleAPI,
  orgActivation: orgActivationAPI,
  //imap: imapAPI,
  STORAGE_KEYS,
};

export default salesPipelineAPI;
